import { useState, useEffect } from 'react';
import { ESMStockFilterOptions } from '@/types/esmStock';
import { getESMStockFilterOptions } from '@/lib/api/esmStocksApi';
import { watchlistApi } from '@/lib/api/watchlistApi';

export function useESMStockFilterOptions() {
  const [filterOptions, setFilterOptions] = useState<ESMStockFilterOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchFilterOptions() {
      try {
        setLoading(true);
        setError(null);

        // Fetch both filter options and watchlists
        const [options, watchlistsResponse] = await Promise.all([
          getESMStockFilterOptions(),
          watchlistApi.listWatchlists()
        ]);

        const watchlists = watchlistsResponse.watchlists.map(w => ({
          id: w.id,
          name: w.name,
          number_of_stocks: (w as typeof w & { number_of_stocks?: number }).number_of_stocks || 0,
          watchlist_type: (w as typeof w & { watchlist_type?: 'default' | 'system_generated' }).watchlist_type || 'default'
        }));

        setFilterOptions({
          ...options,
          watchlists
        });
        setError(null);
      } catch (err) {
        console.error('Failed to load filter options:', err);
        // Fallback to base options without watchlists
        try {
          const options = await getESMStockFilterOptions();
          setFilterOptions({
            ...options,
            watchlists: []
          });
          setError('Failed to load watchlists, but other filters are available');
        } catch (baseErr) {
          const errorMessage = baseErr instanceof Error
            ? baseErr.message
            : 'Failed to load filter options. Please refresh the page.';
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchFilterOptions();
  }, []);

  return {
    filterOptions,
    loading,
    error,
  };
} 